import {initContract} from "@ts-rest/core";
import {z} from "zod";
import {
  addServerSchema,
  ClusterTag,
  MonitorStatus,
  serverSchema,
  ServerSchema,
  ServerUUID,
  ServerName,
  ServerHost,
  updateServerSchema,
  ConfigsRecord,
  CategoryItem,
  noteSchema,
  addNoteSchema,
  NoteID
} from '../types';

const c = initContract();

export const auth = c.router({
  login: {
    method: 'POST',
    path: '/api/v1/auth/login',
    body: z.object({
      username: z.string(),
      password: z.string(),
    }),
    responses: {
      200: c.type<{ token: string }>(),
      401: c.type<{ message: string }>(),
    },
  },
  logout: {
    method: 'POST',
    path: '/api/v1/auth/logout',
    body: z.object({
      token: z.string(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
    },
  },
  status: {
    method: 'GET',
    path: '/api/v1/auth/status',
    headers: z.object({
      authorization: z.string(),
    }),
    responses: {
      200: c.type<{
        isLogged: boolean,
        username: string,
        is_admin: boolean,
      }>(),
      401: c.type<{ message: string }>(),
    },
  },
  update: {
    method: 'PUT',
    path: '/api/v1/auth/update',
    headers: z.object({
      authorization: z.string(),
    }),
    body: z.object({
      oldPassword: z.string(),
      newPassword: z.string(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
    },
  }
});
export const servers = c.router({
  get: {
    method: 'GET',
    path: '/api/v1/servers',
    responses: {
      200: c.type<{
        servers: Array<ServerSchema>,
        cluster: Array<ClusterTag>,
      }>(),
    }
  },
  add: {
    method: 'POST',
    path: '/api/v1/servers',
    body: addServerSchema,
    responses: {
      201: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
    }
  },
  update: {
    method: 'PUT',
    path: '/api/v1/servers/:uuid',
    pathParams: z.object({
      uuid: serverSchema.shape.uuid,
    }),
    body: updateServerSchema,
    responses: {
      200: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    },
  },
  remove: {
    method: 'DELETE',
    path: '/api/v1/servers/:uuid',
    pathParams: z.object({
      uuid: serverSchema.shape.uuid,
    }),
    body: null,
    responses: {
      200: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  action: {
    method: 'POST',
    path: '/api/v1/servers/:uuid/action',
    pathParams: z.object({
      uuid: serverSchema.shape.uuid,
    }),
    body: z.discriminatedUnion('action', [
      z.object({
        action: z.literal('restart'),
      }),
      z.object({
        action: z.literal('updateSslPassword'),
        username: z.string(),
        password: z.string(),
      }),
      z.object({
        action: z.literal('getSslPassword'),
      }),
    ]),
    responses: {
      200: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
    }
  },
  serverAction: {
    method: 'POST',
    path: '/api/v1/servers/action',
    body: z.discriminatedUnion('action', [
      z.object({
        action: z.literal('reinitializeFailed'),
      }),
    ]),
    responses: {
      200: c.type<{ 
        message: string,
        results: Array<{
          serverUUID: ServerUUID,
          serverName: string,
          success: boolean,
          message: string,
        }>,
        summary: {
          total: number,
          success: number,
          failed: number,
        }
      }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
  clusterAction: {
    method: 'GET',
    path: '/api/v1/servers/clusters/:clusterId/action',
    pathParams: z.object({
      clusterId: z.string(),
    }),
    query: z.object({
      type: z.literal('sslUpdatePreview'),
    }),
    responses: {
      200: c.type<{
        impactedServers: Array<{
          uuid: ServerUUID,
          name: ServerName,
          host: ServerHost,
          clusterTag: ClusterTag,
        }>,
        totalServers: number,
        sslEnabledCount: number,
      }>(),
      400: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  clusterActionExecute: {
    method: 'POST',
    path: '/api/v1/servers/clusters/:clusterId/action',
    pathParams: z.object({
      clusterId: z.string(),
    }),
    body: z.discriminatedUnion('action', [
      z.object({
        action: z.literal('sslUpdate'),
        username: z.string(),
        password: z.string(),
      }),
      z.object({
        action: z.literal('sslUpdateSelective'),
        username: z.string(),
        password: z.string(),
        serverUUIDs: z.array(serverSchema.shape.uuid),
      }),
      z.object({
        action: z.literal('sslUpdateSelectivePerServer'),
        username: z.string(),
        serverPasswords: z.array(
          z.object({
            serverUUID: serverSchema.shape.uuid,
            password: z.string(),
          })
        ),
      }),
    ]),
    responses: {
      200: c.type<{
        results: Array<{
          serverUUID: ServerUUID,
          serverName: ServerName,
          success: boolean,
          message: string,
        }>,
        summary: {
          total: number,
          success: number,
          failed: number,
        },
      }>(),
      400: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  getSslPasswords: {
    method: 'POST',
    path: '/api/v1/servers/ssl-passwords',
    body: z.object({
      serverUUIDs: z.array(serverSchema.shape.uuid),
    }),
    responses: {
      200: c.type<{
        passwords: Array<{
          serverUUID: ServerUUID,
          password: string | null,
          error?: string,
        }>,
      }>(),
      400: c.type<{ message: string }>(),
    }
  }
}, {
  baseHeaders: z.object({
    authorization: z.string(),
  })
});
export const serversStats = c.router({
  get: {
    method: 'GET',
    path: '/api/v1/servers/stats',
    responses: {
      200: c.type<{ stats: { [key: ServerUUID]: MonitorStatus } }>(),
    }
  },
  reinitialize: {
    method: 'POST',
    path: '/api/v1/server/:uuid/stats/reinitialize',
    pathParams: z.object({
      uuid: serverSchema.shape.uuid,
    }),
    body: null,
    responses: {
      200: c.type<{ message: string }>(),
    }
  },
  reinitializeFailed: {
    method: 'POST',
    path: '/api/v1/servers/stats/reinitialize-failed',
    body: null,
    responses: {
      200: c.type<{ 
        message: string,
        results: Array<{
          serverUUID: ServerUUID,
          serverName: string,
          success: boolean,
          message: string,
        }>,
        summary: {
          total: number,
          success: number,
          failed: number,
        }
      }>(),
    }
  }
}, {
  baseHeaders: z.object({
    authorization: z.string(),
  })
});


export const users = c.router({
  get: {
    method: 'GET',
    path: '/api/v1/users',
    responses: {
      200: c.type<{
        users: Array<{
          id: number,
          username: string,
          is_active: boolean,
          is_admin: boolean,
          created_at: string,
          updated_at: string,
        }>,
      }>(),
      401: c.type<{ message: string }>(),
    }
  },
  create: {
    method: 'POST',
    path: '/api/v1/users',
    body: z.object({
      username: z.string(),
      password: z.string(),
      is_admin: z.boolean().optional(),
    }),
    responses: {
      201: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
    }
  },
  update: {
    method: 'PUT',
    path: '/api/v1/users/:id',
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      username: z.string().optional(),
      is_active: z.boolean().optional(),
      is_admin: z.boolean().optional(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  delete: {
    method: 'DELETE',
    path: '/api/v1/users/:id',
    pathParams: z.object({
      id: z.string(),
    }),
    body: null,
    responses: {
      200: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  updatePassword: {
    method: 'PUT',
    path: '/api/v1/users/:id/password',
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      newPassword: z.string(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
}, {
  baseHeaders: z.object({
    authorization: z.string(),
  })
});

export const system = c.router({
  get: {
    method: 'GET',
    path: '/api/v1/system',
    responses: {
      200: c.type<{ version: string }>(),
    }
  },
});

export const config = c.router({
  getAll: {
    method: 'GET',
    path: '/api/v1/config',
    responses: {
      200: c.type<{
        configs: ConfigsRecord;
      }>(),
      401: c.type<{ message: string }>(),
    }
  },
  categories: {
    method: 'GET',
    path: '/api/v1/config/categories',
    responses: {
      200: c.type<{
        categories: Array<CategoryItem>;
      }>(),
      401: c.type<{ message: string }>(),
    }
  },
  schema: {
    method: 'GET',
    path: '/api/v1/config/schema',
    responses: {
      200: c.type<{
        schema: Array<{
          key: string;
          defaultValue: string;
          description: string;
          category: string;
          type: string;
          isSecret: boolean;
          isDeprecated: boolean;
        }>;
      }>(),
      401: c.type<{ message: string }>(),
    }
  },
  getByCategory: {
    method: 'GET',
    path: '/api/v1/config/category/:category',
    pathParams: z.object({
      category: z.string(),
    }),
    responses: {
      200: c.type<{
        configs: ConfigsRecord;
      }>(),
      401: c.type<{ message: string }>(),
    }
  },
  get: {
    method: 'GET',
    path: '/api/v1/config/:key',
    pathParams: z.object({
      key: z.string(),
    }),
    responses: {
      200: c.type<{
        key: string;
        value: string;
        description: string;
        category: string;
        type: string;
        isDefault: boolean;
        isSecret: boolean;
        isDeprecated: boolean;
      }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  update: {
    method: 'PUT',
    path: '/api/v1/config/:key',
    pathParams: z.object({
      key: z.string(),
    }),
    body: z.object({
      value: z.string().min(1, 'Value is required'),
    }),
    responses: {
      200: c.type<{
        key: string;
        value: string;
        description: string;
        category: string;
        type: string;
        isDefault: boolean;
        isSecret: boolean;
        isDeprecated: boolean;
        message: string;
      }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  reset: {
    method: 'POST',
    path: '/api/v1/config/:key/reset',
    pathParams: z.object({
      key: z.string(),
    }),
    body: null,
    responses: {
      200: c.type<{
        key: string;
        value: string;
        description: string;
        category: string;
        type: string;
        isDefault: boolean;
        isSecret: boolean;
        isDeprecated: boolean;
        message: string;
      }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
}, {
  baseHeaders: z.object({
    authorization: z.string(),
  })
});

export const status = c.router({
  get: {
    method: 'GET',
    path: '/api/v1/status',
    responses: {
      200: c.type<{
        encryption: {
          status: 'ready' | 'missing' | 'missing_with_data' | 'invalid' | 'system_changed';
          message?: string;
          details?: {
            keyFileExists: boolean;
            systemFingerprintValid: boolean;
            applicationKeyValid: boolean;
            lastValidation: string;
            hasEncryptedData?: boolean;
          };
        };
        // Future status fields can be added here
        // database: { status: 'connected' | 'disconnected' };
        // services: { ssh: boolean; telegram: boolean; monitors: boolean };
      }>(),
      401: c.type<{ message: string }>(),
    },
  },
});

export const encryption = c.router({
  generateKey: {
    method: 'POST',
    path: '/api/v1/encryption/generate-key',
    body: c.type<{ masterPassword: string; confirmPassword: string }>(),
    responses: {
      200: c.type<{ message: string; recoveryCode: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    },
  },
  recoverKey: {
    method: 'POST',
    path: '/api/v1/encryption/recover-key',
    body: c.type<{ recoveryCode: string; masterPassword: string }>(),
    responses: {
      200: c.type<{ message: string; newRecoveryCode: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    },
  },
});

export const notes = c.router({
  get: {
    method: 'GET',
    path: '/api/v1/notes',
    responses: {
      200: c.type<{
        notes: Array<{
          id: number;
          content: string;
          creator_id: number;
          creator_username: string;
          is_resolved: boolean;
          created_at: string;
          resolved_at: string | null;
        }>;
        unresolvedCount: number;
      }>(),
      401: c.type<{ message: string }>(),
    }
  },
  create: {
    method: 'POST',
    path: '/api/v1/notes',
    body: addNoteSchema,
    responses: {
      201: c.type<{ message: string; note: {
        id: number;
        content: string;
        creator_id: number;
        creator_username: string;
        is_resolved: boolean;
        created_at: string;
        resolved_at: string | null;
      } }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
    }
  },
  resolve: {
    method: 'PUT',
    path: '/api/v1/notes/:id/resolve',
    pathParams: z.object({
      id: z.string(),
    }),
    body: null,
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
  delete: {
    method: 'DELETE',
    path: '/api/v1/notes/:id',
    pathParams: z.object({
      id: z.string(),
    }),
    body: null,
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      404: c.type<{ message: string }>(),
    }
  },
}, {
  baseHeaders: z.object({
    authorization: z.string(),
  })
});

export const sftp = c.router({
  list: {
    method: 'GET',
    path: '/api/v1/sftp/:uuid/list',
    pathParams: z.object({
      uuid: z.string(),
    }),
    query: z.object({
      path: z.string(),
    }),
    responses: {
      200: c.type<{
        items: Array<{
          name: string;
          path: string;
          type: 'file' | 'directory' | 'symlink';
          size: number;
          modifiedDate: string;
          permissions: string;
          owner?: string;
          group?: string;
        }>;
        currentPath: string;
      }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
  readFile: {
    method: 'GET',
    path: '/api/v1/sftp/:uuid/read',
    pathParams: z.object({
      uuid: z.string(),
    }),
    query: z.object({
      path: z.string(),
    }),
    responses: {
      200: c.type<{ content: string; path: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
  writeFile: {
    method: 'POST',
    path: '/api/v1/sftp/:uuid/write',
    pathParams: z.object({
      uuid: z.string(),
    }),
    body: z.object({
      path: z.string(),
      content: z.string(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
  delete: {
    method: 'DELETE',
    path: '/api/v1/sftp/:uuid/delete',
    pathParams: z.object({
      uuid: z.string(),
    }),
    body: z.object({
      path: z.string(),
      isDirectory: z.boolean(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
  createDirectory: {
    method: 'POST',
    path: '/api/v1/sftp/:uuid/mkdir',
    pathParams: z.object({
      uuid: z.string(),
    }),
    body: z.object({
      path: z.string(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
  rename: {
    method: 'POST',
    path: '/api/v1/sftp/:uuid/rename',
    pathParams: z.object({
      uuid: z.string(),
    }),
    body: z.object({
      oldPath: z.string(),
      newPath: z.string(),
    }),
    headers: z.object({
      authorization: z.string(),
    }),
    responses: {
      200: c.type<{ message: string }>(),
      400: c.type<{ message: string }>(),
      401: c.type<{ message: string }>(),
      500: c.type<{ message: string }>(),
    }
  },
}, {
  baseHeaders: z.object({
    authorization: z.string(),
  })
});

export const contract = c.router({
  auth,
  servers,
  serversStats,
  users,
  system,
  config,
  status,
  encryption,
  notes,
  sftp, // NEW: SFTP endpoints
});
